/**
 * 日期推算、周期名称等纯函数（不依赖 Vue 实例）
 */
export function parsePeriodDate (val) {
  if (val == null || val === '') return null;
  let d;
  if (val instanceof Date) {
    d = new Date(val.getTime());
  } else if (typeof val === 'number' && Number.isFinite(val)) {
    d = new Date(val);
  } else {
    d = new Date(val);
  }
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getTaskYearRange (cycleType, taskYear) {
  if (cycleType === 'day' || cycleType === 'special' || taskYear === null || taskYear === undefined || taskYear === '') {
    return null;
  }
  const year = Number(taskYear);
  if (!Number.isFinite(year)) return null;
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31)
  };
}

export function isDateInTaskYear (date, cycleType, taskYear) {
  const range = getTaskYearRange(cycleType, taskYear);
  if (!range) return true;
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return target >= range.start && target <= range.end;
}

export function normalizeDateToTaskYear (date, cycleType, taskYear) {
  const range = getTaskYearRange(cycleType, taskYear);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  if (!range) return target;
  if (target < range.start) return new Date(range.start);
  if (target > range.end) return new Date(range.end);
  return target;
}

export function getMaxExecuteDateAmongPeriods (periodConfigs) {
  let max = null;
  for (const p of periodConfigs) {
    const d = parsePeriodDate(p.executeDate);
    if (!d) continue;
    if (!max || d > max) max = d;
  }
  return max;
}

export function getNextDate (date, type) {
  const d = new Date(date);
  if (type === 'month') {
    d.setMonth(d.getMonth() + 1);
  } else if (type === 'quarter') {
    d.setMonth(d.getMonth() + 3);
  } else if (type === 'year' || type === 'yearly_daily') {
    d.setFullYear(d.getFullYear() + 1);
  } else if (type === 'half_year') {
    d.setMonth(d.getMonth() + 6);
  } else {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

export function generatePeriodName (date, type) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const day = date.getDate();

  if (type === 'day') {
    return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  if (type === 'month') {
    return `${y}年${m}月`;
  }
  if (type === 'quarter') {
    const q = Math.ceil(m / 3);
    return `${y}年第${q}季度`;
  }
  if (type === 'year' || type === 'yearly_daily') {
    return `${y}年`;
  }
  if (type === 'half_year') {
    return m <= 6 ? `${y}年上半年` : `${y}年下半年`;
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * 第一个任务周期的默认「计划开始日期」
 */
export function getDefaultPeriodStartDate (cycleType, taskYear, now = new Date()) {
  if (cycleType === 'day' || cycleType === 'special') {
    return new Date();
  }
  const raw = taskYear;
  const year = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (!Number.isFinite(year)) {
    return new Date();
  }
  const cy = now.getFullYear();
  if (year > cy) {
    return new Date(year, 0, 1);
  }
  if (year === cy) {
    if (cycleType === 'half_year') {
      const cm = now.getMonth();
      return cm < 6 ? new Date(cy, 0, 1) : new Date(cy, 6, 1);
    }
    return new Date(cy, now.getMonth(), 1);
  }
  return new Date(year, 0, 1);
}

/**
 * 年度/年度日查 + 按月分解：从指定月份排到 12 月
 * @returns {Array|null} taskYear 无效时返回 null
 */
export function buildMonthlySplitPeriodConfigs (taskYear, now = new Date()) {
  const raw = taskYear;
  // null/undefined 时 Number(null)===0 会错生成「0年M月」，须显式排除
  if (raw === null || raw === undefined || raw === '') return null;
  const y = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (!Number.isFinite(y) || y < 1) return null;
  const cy = now.getFullYear();
  let startMonth = 0;
  if (y === cy) {
    startMonth = now.getMonth();
  }
  const endMonth = 11;
  const periodConfigs = [];
  for (let m = startMonth; m <= endMonth; m++) {
    const executeDate = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    periodConfigs.push({
      periodName: `${y}年${m + 1}月`,
      executeDate,
      deadlineDate: lastDay,
      systemConfigs: []
    });
  }
  return periodConfigs;
}

/**
 * 任务期次下拉：任务所属年份为「当前自然年」时，已过去的月份不再提供。
 * @param {number} taskYear 任务组年份
 * @param {number} monthIndex0 月份 0-11（与 Date 一致）
 */
export function isTaskYearMonthSelectable (taskYear, monthIndex0, now = new Date()) {
  const y = typeof taskYear === 'string' ? parseInt(taskYear, 10) : Number(taskYear);
  if (!Number.isFinite(y)) return true;
  if (y !== now.getFullYear()) return true;
  return monthIndex0 >= now.getMonth();
}

/**
 * 第 q 季度（1-4）已整体结束则不可选：当前月份大于该季最后一个月后即不可选。
 */
export function isTaskYearQuarterSelectable (taskYear, quarter1to4, now = new Date()) {
  const y = typeof taskYear === 'string' ? parseInt(taskYear, 10) : Number(taskYear);
  if (!Number.isFinite(y)) return true;
  if (y !== now.getFullYear()) return true;
  const q = quarter1to4;
  return now.getMonth() + 1 <= 3 * q;
}

/**
 * 上半年：七月起视为已过去；下半年在同一年内始终可选（预排下半年任务）。
 */
export function isTaskYearFirstHalfSelectable (taskYear, now = new Date()) {
  const y = typeof taskYear === 'string' ? parseInt(taskYear, 10) : Number(taskYear);
  if (!Number.isFinite(y)) return true;
  if (y !== now.getFullYear()) return true;
  return now.getMonth() + 1 <= 6;
}
